import { useCallback, useEffect, useRef, useState } from 'react';

// Keep keystrokes in the native input. The room/message list only needs to
// render when the send button changes between empty and non-empty.
export default function useChatComposer() {
  const inputRef = useRef(null);
  const draftRef = useRef('');
  const hasTextRef = useRef(false);
  const composingRef = useRef(false);
  const resizeFrameRef = useRef(null);
  const [hasText, setHasText] = useState(false);

  const scheduleResize = useCallback(() => {
    if (resizeFrameRef.current !== null) return;
    resizeFrameRef.current = requestAnimationFrame(() => {
      resizeFrameRef.current = null;
      const element = inputRef.current;
      if (!element) return;
      element.style.height = 'auto';
      const height = element.scrollHeight;
      element.style.height = `${Math.min(132, Math.max(40, height))}px`;
      element.style.overflowY = height > 132 ? 'auto' : 'hidden';
    });
  }, []);

  const syncValue = useCallback((value) => {
    draftRef.current = value;
    const nextHasText = Boolean(value.trim());
    if (hasTextRef.current !== nextHasText) {
      hasTextRef.current = nextHasText;
      setHasText(nextHasText);
    }
    scheduleResize();
  }, [scheduleResize]);

  const attachInput = useCallback((element) => {
    inputRef.current = element;
    if (element) {
      element.value = draftRef.current;
      scheduleResize();
    }
  }, [scheduleResize]);

  const readText = useCallback(() => inputRef.current?.value ?? draftRef.current, []);
  const writeText = useCallback((nextValue) => {
    const value = String(typeof nextValue === 'function' ? nextValue(readText()) : nextValue);
    if (inputRef.current) inputRef.current.value = value;
    syncValue(value);
  }, [readText, syncValue]);

  const onChange = useCallback((event) => syncValue(event.target.value), [syncValue]);
  const onCompositionStart = useCallback(() => { composingRef.current = true; }, []);
  const onCompositionEnd = useCallback((event) => {
    composingRef.current = false;
    syncValue(event.currentTarget.value);
  }, [syncValue]);
  const shouldSendOnEnter = useCallback((event) => (
    event.key === 'Enter'
    && !event.shiftKey
    && !composingRef.current
    && !event.nativeEvent?.isComposing
    && event.nativeEvent?.keyCode !== 229
  ), []);

  useEffect(() => {
    // Rotation and keyboard resizing can change line wrapping without input.
    window.addEventListener('resize', scheduleResize);
    scheduleResize();
    return () => {
      window.removeEventListener('resize', scheduleResize);
      if (resizeFrameRef.current !== null) cancelAnimationFrame(resizeFrameRef.current);
      resizeFrameRef.current = null;
    };
  }, [scheduleResize]);

  return {
    inputRef, attachInput, hasText, readText, writeText, onChange,
    onCompositionStart, onCompositionEnd, shouldSendOnEnter,
  };
}
